import { createTheme } from '@material-ui/core';

import BebasNeueRegularWoff2 from './fonts/BebasNeue/BebasNeue-Regular.woff2';
import BebasNeueRegularWoff from './fonts/BebasNeue/BebasNeue-Regular.woff';
import BebasNeueRegularTrueType from './fonts/BebasNeue/BebasNeue-Regular.ttf';
import BebasNeueBoldWoff2 from './fonts/BebasNeue/BebasNeue-Bold.woff2';
import BebasNeueBoldWoff from './fonts/BebasNeue/BebasNeue-Bold.woff';
import BebasNeueBoldTrueType from './fonts/BebasNeue/BebasNeue-Bold.ttf';


declare module '@material-ui/core/styles/createTheme' {
  interface Theme {
    sidebarWidth: number;
    sidebarMobileHeight: number;
    whiteBasic: string;
    trueblackBasic: string;
    brand: string;
    grey: string;
    greyMain: string;
    greyLight: string;
    greenMain: string;
    red: string;
    footerHeight: number;
    mobileTopBarHeight: number;
    mobileFooterHeight: number;
    sidebarMobilePadding: number;
    participantBorderWidth: number;
    rightDrawerWidth: number;
    galleryViewBackgroundColor: string;
  }

  // allow configuration using `createMuiTheme`
  interface ThemeOptions {
    components?: object;
    sidebarWidth?: number;
    sidebarMobileHeight?: number;
    whiteBasic: string;
    trueblackBasic: string;
    brand: string;
    grey: string;
    greyMain: string;
    greyLight: string;
    greenMain: string;
    red: string;
    footerHeight: number;
    mobileTopBarHeight: number;
    mobileFooterHeight: number;
    sidebarMobilePadding: number;
    participantBorderWidth: number;
    rightDrawerWidth?: number;
    galleryViewBackgroundColor: string;
  }
}

const BebasNeueRegular = {
  fontFamily: "BebasNeue",
  fontStyle: "normal",
  fontWeight: 300,
  src: `
    local('BebasNeue'),
    local('BebasNeue-Regular'),
    url(${BebasNeueRegularTrueType}) format("truetype"),
    url(${BebasNeueRegularWoff2}) format("woff2"),
    url(${BebasNeueRegularWoff}) format("woff")
  `
};

const BebasNeueBold = {
  fontFamily: "BebasNeue",
  fontStyle: "normal",
  fontWeight: 700,
  src: `
    local('BebasNeue'),
    local('BebasNeue-Bold'),
    url(${BebasNeueBoldTrueType}) format("truetype"),
    url(${BebasNeueBoldWoff2}) format("woff2"),
    url(${BebasNeueBoldWoff}) format("woff")
  `
};

const defaultTheme = createTheme();

export default createTheme({
  overrides: {
    MuiCssBaseline: {
      '@global': {
        'html, body, #root': {
          height: '100%',
        },
        'body': {
          fontSize: '16px',
        },
        '@font-face': [BebasNeueRegular, BebasNeueBold],
      },
    },
    MuiButton: {
      root: {
        borderRadius: '4px',
        textTransform: 'none',
        color: 'rgb(40, 42, 43)',
        fontSize: '0.9rem',
        transition: defaultTheme.transitions.create(['background-color', 'box-shadow', 'border', 'color'], {
          duration: defaultTheme.transitions.duration.short,
        }),
      },
      text: {
        padding: '6px 14px',
      },
      contained: {
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none',
        },
      },
      outlinedPrimary: {
        border: '2px solid #027AC5',
        '&:hover': {
          border: '2px solid rgb(1, 85, 137)',
        },
      },
      startIcon: {
        marginRight: '6px',
      },
    },
    MuiTypography: {
      body1: {
        color: 'rgb(40, 42, 43)',
        fontSize: '0.9rem',
      },
    },
    MuiInputBase: {
      root: {
        fontSize: '0.9rem',
      },
    },
    MuiSelect: {
      root: {
        padding: '0.85em',
      },
    },
    MuiDialogActions: {
      root: {
        padding: '16px',
      },
    },
    MuiTextField: {
      root: {
        color: 'rgb(40, 42, 43)',
      },
    },
    MuiInputLabel: {
      root: {
        color: 'rgb(40, 42, 43)',
        fontSize: '1.1rem',
        marginBottom: '0.2em',
        fontWeight: 500,
      },
    },
    MuiOutlinedInput: {
      notchedOutline: {
        borderColor: 'rgb(136, 140, 142)',
      },
    },
  },
  typography: {
    fontFamily: [
      'BebasNeue',
      'AvenirNextCondensed-Bold',
      'Futura-CondensedExtraBold',
      'HelveticaNeue-CondensedBold',
      'Arial Narrow',
      'Helvetica Neue',
      'sans-serif-condensed',
      'sans-serif',
    ].join(','),
    body1: {
      fontFamily: "'Poppins', Arial, sans-serif",
      fontSize: 16,
      fontWeight: 400,
      lineHeight: '26px'
    },
    /* .tbk-text-small */
    subtitle1: {
      fontSize: 12,
      fontFamily: "'Poppins', Arial, sans-serif",
      lineHeight: '18px',
      fontWeight: 400,
    },
    /* .tbk-text-small-bold */
    caption: {
      fontSize: 12,
      fontFamily: "'Poppins', Arial, sans-serif",
      lineHeight: '14px',
      fontWeight: 700,
    },
    /* H1 Headline */
    h1: {
      // fontFamily: 'BebasNeue',
      fontWeight: 700,
      fontSize: 60,
      lineHeight: '60px'
    },
    /* .tbk-text-medium-headline */
    h2: {
      fontSize: 40,
      // fontFamily: "'Poppins', Arial, sans-serif",
      lineHeight: '40px',
      fontWeight: 700,
    },
    /* .tbk-text-h3-subtitle */
    h3: {
      fontSize: 18,
      fontFamily: "'Poppins', Arial, sans-serif",
      lineHeight: '26px',
      fontWeight: 600,
    },
    /* Navigation Narrow */
    h4: {
      fontSize: 26,
      // fontFamily: "'Poppins', Arial, sans-serif",
      lineHeight: '26px',
      fontWeight: 700,
    },
    /* .tbk-text-epic-medium */
    h5: {
      fontSize: 22,
      fontFamily: "'Poppins', Arial, sans-serif",
      lineHeight: '33px',
      fontWeight: 400,
    },
    /* .tbk-text-cta */
    button: {
      fontFamily: 'Poppins',
      fontWeight: 600,
      fontSize: 18,
      lineHeight: '26px'
    },
    /* .tbk-text-main-linked */
    subtitle2: {
      fontFamily: 'Poppins',
      fontWeight: 400,
      fontSize: 16,
      lineHeight: '26px'
    },

  },
  palette: {
    primary: {
      main: '#303740' // '#027AC5',
    },
    secondary: {
      main: '#ffffff' // '#027AC5',
    },
  },
  brand: '#E22525',
  grey: '#6B7178',
  greyMain: '#303740',
  greyLight: '#F8F8F8',
  red: '#E94E32',
  whiteBasic: '#ffffff',
  trueblackBasic: '#000000',
  greenMain: '#3BC693',
  footerHeight: 72,
  mobileFooterHeight: 56,
  sidebarWidth: 300,
  sidebarMobileHeight: 90,
  sidebarMobilePadding: 8,
  participantBorderWidth: 2,
  mobileTopBarHeight: 52,
  rightDrawerWidth: 320,
  galleryViewBackgroundColor: '#121C2D',
});
